import React, { useState } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  makeStyles,
  Box,
  Typography,
  InputLabel,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import SettingsIcon from "@material-ui/icons/Settings";
import { InfoOutlined as InfoIcon } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import PopoverInfoSection from "./PopoverInfoSection";
import { CALCULATION_METHOD_OPTIONS_COCKPIT } from "services/utils/calculationConstants";

const useStyles = (clientColor) =>
  makeStyles((theme) => ({
    ...customSelectStyle(theme, clientColor),
    sectionContainer: {
      border: `1px solid ${grayColor[8]}`,
      borderLeft: `4px solid ${clientColor || theme.palette.primary.main}`,
      borderRadius: theme.spacing(1),
      padding: theme.spacing(2),
      backgroundColor: grayColor[12],
      position: "relative",
    },
    sectionTitle: {
      fontSize: "0.95rem",
      fontWeight: 500,
      marginBottom: theme.spacing(2),
      color: clientColor || theme.palette.primary.main,
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(0.5),
    },
    sectionIcon: {
      fontSize: "1rem",
      opacity: 0.7,
      color: clientColor || theme.palette.primary.main,
    },
    iconButton: {
      padding: theme.spacing(0.75),
      color: theme.palette.text.secondary,
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
        color: theme.palette.text.primary,
      },
    },
    contentRow: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(2),
      flexWrap: "wrap",
    },
    selectorControl: {
      minWidth: 170,
      flex: 1,
      "& > div": {
        "&:before": {
          borderBottomWidth: "1px !important",
          borderBottomColor: `${grayColor[4]}!important`,
        },
        "&:after": {
          borderBottomColor: `${clientColor}!important`,
        },
      },
      "& .MuiInputLabel-root": {
        "&.Mui-focused": {
          color: `${clientColor}!important`,
        },
      },
    },
    premiseWithInfo: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flex: 1,
      minWidth: 170,
    },
  }));

export default function SectionScenarioSpec({
  calculationMethod,
  onCalculationMethodChange,
  scenarioPremises,
  scenarioPremisesValue,
  onScenarioPremisesChange,
  taxRegimes,
  taxRegimeValue,
  onTaxRegimeChange,
  clientColor,
}) {
  const classes = useStyles(clientColor)();
  const T_PREFIX = "rgm.cockpit.scenarioCreation";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  const [infoPopoverAnchor, setInfoPopoverAnchor] = useState(null);

  const handleInfoClick = (event) => {
    setInfoPopoverAnchor(event.currentTarget);
  };

  const handleInfoClose = () => {
    setInfoPopoverAnchor(null);
  };

  const selectedPremise = scenarioPremises.find(
    (p) => p.premise_key === scenarioPremisesValue,
  );

  return (
    <Box className={classes.sectionContainer}>
      {/* Section Title */}
      <Typography className={classes.sectionTitle}>
        <SettingsIcon className={classes.sectionIcon} />
        {t("scenario_specifications")}
      </Typography>

      {/* Dropdowns */}
      <Box className={classes.contentRow}>
        {/* Regime tributário (reforma), o primeiro a escolher: só quando a implementação oferece mais de um */}
        {taxRegimes && taxRegimes.length > 1 && (
          <FormControl className={classes.selectorControl}>
            <InputLabel>{t("tax_regime")}</InputLabel>
            <Select
              value={taxRegimeValue}
              onChange={onTaxRegimeChange}
              MenuProps={{
                className: classes.selectMenu,
                PaperProps: {
                  className: classes.selectPaper,
                  style: { maxHeight: 300 },
                },
              }}
            >
              {taxRegimes.map((regime) => (
                <MenuItem
                  key={regime.code}
                  value={regime.code}
                  classes={{
                    root: classes.selectMenuItem,
                    selected: classes.selectMenuItemSelected,
                  }}
                >
                  {regime.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Calculation Method */}
        <FormControl className={classes.selectorControl}>
          <InputLabel>{t("calculation_method")}</InputLabel>
          <Select
            value={calculationMethod}
            onChange={onCalculationMethodChange}
            MenuProps={{
              className: classes.selectMenu,
              PaperProps: {
                className: classes.selectPaper,
                style: { maxHeight: 300 },
              },
            }}
          >
            {CALCULATION_METHOD_OPTIONS_COCKPIT.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                classes={{
                  root: classes.selectMenuItem,
                  selected: classes.selectMenuItemSelected,
                }}
              >
                {t(option.labelKey, { keyPrefix: "rgm.inputs" })}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Scenario Premises with Info Button */}
        {scenarioPremises && scenarioPremises.length > 0 && (
          <Box className={classes.premiseWithInfo}>
            <FormControl className={classes.selectorControl}>
              <InputLabel>{t("scenario_premises")}</InputLabel>
              <Select
                value={scenarioPremisesValue}
                onChange={onScenarioPremisesChange}
                MenuProps={{
                  className: classes.selectMenu,
                  PaperProps: {
                    className: classes.selectPaper,
                    style: { maxHeight: 300 },
                  },
                }}
              >
                {scenarioPremises.map((premise) => (
                  <MenuItem
                    key={premise.premise_key}
                    value={premise.premise_key}
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                  >
                    {premise.premise_label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedPremise?.premise_description && (
              <Tooltip title={t("scenario_premises_info_tooltip")}>
                <IconButton
                  onClick={handleInfoClick}
                  className={classes.iconButton}
                  size="small"
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {/* Info Popover */}
      {selectedPremise?.premise_description && (
        <PopoverInfoSection
          open={Boolean(infoPopoverAnchor)}
          anchorEl={infoPopoverAnchor}
          onClose={handleInfoClose}
          htmlContent={selectedPremise.premise_description}
        />
      )}
    </Box>
  );
}

SectionScenarioSpec.propTypes = {
  calculationMethod: PropTypes.string.isRequired,
  onCalculationMethodChange: PropTypes.func.isRequired,
  scenarioPremises: PropTypes.arrayOf(
    PropTypes.shape({
      premise_key: PropTypes.string.isRequired,
      premise_label: PropTypes.string.isRequired,
      premise_description: PropTypes.string,
    }),
  ),
  scenarioPremisesValue: PropTypes.string,
  onScenarioPremisesChange: PropTypes.func,
  taxRegimes: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
  taxRegimeValue: PropTypes.string,
  onTaxRegimeChange: PropTypes.func,
  clientColor: PropTypes.string,
};
